import type { FaireAdvertiserProfileEntity } from "../../entity_types/advertiserProfile";

// Dairy Brand (b_2dvofcgxz7, advertiser 8749774711315878893).
// Real numbers from ANALYTICS.ADS.CAMPAIGN_PERIODS (lifetime totals over the 31
// complete months Feb 2024 – Aug 2026). Monthly series + partial Sep 2026 live in
// realBrandData.ts.
//
// The high-budget-strong-results archetype, and by some distance the largest
// program in the set: $65k of spend returning $454k of sales from 1,075
// conversions, where the next biggest variant is Zotter at $7k and 82. It is the
// only variant whose reorder campaign is a standing part of the mix rather than
// an experiment — 412 reorder conversions still running, against Zotter's 13
// that stopped — so it's the profile to reach for when the reorder half of the
// page needs enough data to look at.
//
// It is also the first POOLED advertiser here: `sharedBudgetDollars` is set and
// both campaigns draw on the same $6,000 rather than holding budgets of their
// own. `totalBudgetDollars` is that same pool, not a sum, which is what the
// "monthly budget of $X" page header should read.
//
// `spendingMode` is real (ADS_SERVER.ADVERTISERS.spending_mode), unlike the
// variants where it's set only to give the growth-mode module a default.
export const advDairyBrand: FaireAdvertiserProfileEntity = {
  token: "adv_dairy_brand",
  type: "advertiserProfile",
  archetype: "high-budget-strong-results",
  displayName: "Dairy Brand",
  brandToken: "b_2dvofcgxz7",
  audienceType: "NEW_AND_RETURNING",
  globalStatus: "ACTIVE",
  tenureMonths: 32,
  budget: {
    totalBudgetDollars: 6000,
    sharedBudgetDollars: 6000,
    spendingMode: "COMPETITIVE",
    nextRenewsAtDate: "2026-10-01",
    nextBillsAtDate: "2026-10-04",
  },
  overview: {
    numCustomersAcquired: 663,
    numReorderConversions: 412,
    numTotalConversions: 1075,
    // Cohort-basis sales: each acquisition month's first orders plus the reorders
    // those retailers placed over the following twelve months. This is the copy
    // the page actually reads (`getP8Profile().overview`), so it has to match the
    // sum of DAIRY_BRAND.months in realBrandData.ts — $378,633 on the new segment
    // — rather than the platform's uncapped lifetime rollup of $455,724.
    //
    // The ROAS fields below cannot be reconstructed from these: they run off
    // first-year value ($398,889 new), which is a projection and sits *below*
    // booked sales here because most cohorts are years past their first twelve
    // months and have outrun what the first year predicted.
    totalAdsSalesDollars: 454071,
    newCustomerAdsSalesDollars: 378633,
    reorderAdsSalesDollars: 75439,
    totalAdsSpendDollars: 64938,
    newCustomerAdsSpendDollars: 54215,
    reorderAdsSpendDollars: 10723,
    newCustomerBestEstimateFirstYearRoas: 7.36,
    reorderRoas: 7.03,
    combinedFirstYearRoas: 7.3,
    // MANAGER_METRICS_BRAND_ORDERS: 2,707 orders on the new-customer campaign
    // (from 663 acquired customers) and 459 on the reorder campaign.
    numNewCustomerAdsOrders: 2707,
    numReorderAdsOrders: 459,
  },
  availableCredit: {
    totalRemainingDollars: 0,
  },
  // MANAGER_METRICS_BRANDS (ds 2026-09-16), budget_status_tag "limited_20_30",
  // 12-month LTV $680.16. Both segments are HEADROOM_RECOMMENDATION_TYPE with
  // is_eligible_for_increase_recommendation false and
  // HEADROOM_BASED_RECS_LESS_THAN_BUDGET — the headroom ranges sit under the
  // $6,000 pool, so these describe capacity rather than prompt an increase.
  budgetRecommendations: {
    newCustomer: {
      minRecommendedBudgetDollars: 2490,
      maxRecommendedBudgetDollars: 4490,
      isEligibleForIncreaseRec: false,
    },
    reorder: {
      minRecommendedBudgetDollars: 1480,
      maxRecommendedBudgetDollars: 2960,
    },
  },
  campaignTokens: ["camp_db_new", "camp_db_reorder"],
  overviewPerformancePeriodTokens: [],
  meta: {
    source: "snowflake",
    importedAt: "2026-09-19T00:00:00.000Z",
    importedBy: "real-brand-data",
  },
};
